import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useScreenRenderContext, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef } from '@micromag/core/contexts';
import { isTextFilled, isHeaderFilled, isFooterFilled, getFooterProps, getStyleFromBox } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout, { Spacer } from '@micromag/element-layout';
import Text from '@micromag/element-text';
import _objectDestructuringEmpty from '@babel/runtime/helpers/objectDestructuringEmpty';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';

var styles = {"container":"micromag-screen-title-container","background":"micromag-screen-title-background","content":"micromag-screen-title-content","emptyTitle":"micromag-screen-title-emptyTitle","emptySubtitle":"micromag-screen-title-emptySubtitle","emptyDescription":"micromag-screen-title-emptyDescription","title":"micromag-screen-title-title","withMargin":"micromag-screen-title-withMargin","subtitle":"micromag-screen-title-subtitle","box":"micromag-screen-title-box","isPlaceholder":"micromag-screen-title-isPlaceholder","layout":"micromag-screen-title-layout"};

var propTypes$2 = {
  layout: PropTypes.oneOf(['top', 'middle', 'bottom', 'split', 'split-top', 'split-bottom']),
  title: PropTypes$1.headingElement,
  subtitle: PropTypes$1.headingElement,
  description: PropTypes$1.textElement,
  boxStyle: PropTypes$1.boxStyle,
  withSubtitle: PropTypes.bool,
  withDescription: PropTypes.bool,
  withBox: PropTypes.bool,
  spacing: PropTypes.number,
  descriptionEmptyLabel: PropTypes$1.label,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  background: PropTypes$1.backgroundElement,
  current: PropTypes.bool,
  active: PropTypes.bool,
  transitionStagger: PropTypes.number,
  className: PropTypes.string
};
var defaultProps$2 = {
  layout: 'top',
  title: null,
  subtitle: null,
  description: null,
  boxStyle: null,
  withSubtitle: false,
  withDescription: false,
  withBox: false,
  spacing: 20,
  descriptionEmptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "507VAi",
    defaultMessage: [{
      "type": 0,
      "value": "Description"
    }]
  }),
  header: null,
  footer: null,
  background: null,
  current: true,
  active: true,
  transitionStagger: 100,
  className: null
};
var TitleScreen = function TitleScreen(_ref) {
  var layout = _ref.layout,
    title = _ref.title,
    subtitle = _ref.subtitle,
    description = _ref.description,
    boxStyle = _ref.boxStyle,
    withSubtitle = _ref.withSubtitle,
    withDescription = _ref.withDescription,
    withBox = _ref.withBox,
    spacing = _ref.spacing,
    descriptionEmptyLabel = _ref.descriptionEmptyLabel,
    header = _ref.header,
    footer = _ref.footer,
    background = _ref.background,
    current = _ref.current,
    active = _ref.active,
    className = _ref.className;
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var hasTitle = isTextFilled(title);
  var hasSubtitle = isTextFilled(subtitle);
  var hasDescription = isTextFilled(description);
  var layoutParts = layout.split('-');
  var isSplitted = layoutParts[0] === 'split';
  var isTopLayout = layout === 'top';
  var isMiddleLayout = layout === 'middle';
  var isBottomLayout = layout === 'bottom';
  var verticalAlign = isSplitted ? layoutParts[1] || null : layoutParts[0];
  var titleWithMargin = hasTitle && (hasSubtitle || hasDescription) && (!isSplitted || verticalAlign === 'top');
  var subtitleWithMargin = hasSubtitle && hasDescription && (!isSplitted || verticalAlign === 'bottom');
  var backgroundPlaying = current && (isView || isEdit);
  var backgroundShouldLoad = current || active;
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var titleElement = /*#__PURE__*/React.createElement(ScreenElement, {
    key: "title",
    placeholder: "title",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "2ZOPe+",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    emptyClassName: styles.emptyTitle,
    isEmpty: !hasTitle
  }, hasTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: classNames([styles.title, _defineProperty({}, styles.withMargin, titleWithMargin)])
  }, title, {
    size: 1
  })) : null);
  var subtitleElement = withSubtitle ? /*#__PURE__*/React.createElement(ScreenElement, {
    key: "subtitle",
    placeholder: "subtitle",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "d60tSn",
      defaultMessage: [{
        "type": 0,
        "value": "Subtitle"
      }]
    }),
    emptyClassName: styles.emptySubtitle,
    isEmpty: !hasSubtitle
  }, hasSubtitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: classNames([styles.subtitle, _defineProperty({}, styles.withMargin, subtitleWithMargin)])
  }, subtitle, {
    size: 2
  })) : null) : null;
  var descriptionElement = withDescription ? /*#__PURE__*/React.createElement(ScreenElement, {
    key: "description",
    placeholder: "shortText",
    emptyLabel: descriptionEmptyLabel,
    emptyClassName: styles.emptyDescription,
    isEmpty: !hasDescription
  }, hasDescription ? /*#__PURE__*/React.createElement(Text, description) : null) : null;
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": true
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    fullscreen: true,
    verticalAlign: verticalAlign,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: (!isPreview ? viewerTopHeight : 0) + (hasHeader ? spacing / 2 : spacing),
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + (hasFooter ? spacing / 2 : spacing)
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    style: {
      paddingBottom: spacing
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, !isPlaceholder && hasFooter && isMiddleLayout ? /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer-cta-top"
  }) : null, !isPlaceholder && hasHeader && isBottomLayout ? /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer-cta-top"
  }) : null, !isPlaceholder && hasHeader && !hasFooter && isMiddleLayout ? /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer-cta-top"
  }) : null, withBox && !isSplitted ? /*#__PURE__*/React.createElement("div", {
    className: styles.box,
    style: isEdit || isPlaceholder || hasTitle || hasSubtitle || hasDescription ? getStyleFromBox(boxStyle) : null
  }, [titleElement, subtitleElement, descriptionElement]) : null, withBox && isSplitted ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.box,
    key: "top",
    style: isEdit || isPlaceholder || hasTitle || withDescription && verticalAlign === 'top' && hasSubtitle ? getStyleFromBox(boxStyle) : null
  }, titleElement, withDescription && verticalAlign === 'top' ? subtitleElement : null), /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer1"
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.box,
    key: "bottom",
    style: isEdit || isPlaceholder || hasDescription || (!withDescription || verticalAlign === 'bottom') && hasSubtitle ? getStyleFromBox(boxStyle) : null
  }, !withDescription || verticalAlign === 'bottom' ? subtitleElement : null, descriptionElement)) : null, !withBox ? /*#__PURE__*/React.createElement(React.Fragment, null, titleElement, isSplitted && (!withDescription || verticalAlign === 'bottom') && /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer1"
  }), subtitleElement, isSplitted && withDescription && verticalAlign === 'top' && /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer2"
  }), descriptionElement) : null, !isPlaceholder && hasFooter && (isTopLayout || isMiddleLayout) ? /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer-cta-bottom"
  }) : null, !isPlaceholder && hasHeader && !hasFooter && isMiddleLayout ? /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer-cta-bottom"
  }) : null, !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    key: "call-to-action",
    style: {
      paddingTop: spacing,
      paddingLeft: Math.max(0, viewerBottomSidesWidth - spacing),
      paddingRight: Math.max(0, viewerBottomSidesWidth - spacing)
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: backgroundShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
TitleScreen.propTypes = propTypes$2;
TitleScreen.defaultProps = defaultProps$2;

var TitleSubtitleScreen = function TitleSubtitleScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(TitleScreen, Object.assign({}, props, {
    withSubtitle: true
  }));
};

var _excluded = ["credits"];
var propTypes$1 = {
  credits: PropTypes$1.textElement
};
var defaultProps$1 = {
  credits: null
};
var TitleSubtitleCreditsScreen = function TitleSubtitleCreditsScreen(_ref) {
  var credits = _ref.credits,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(TitleScreen, Object.assign({}, props, {
    description: credits,
    withSubtitle: true,
    withDescription: true,
    descriptionEmptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "/hupzO",
      defaultMessage: [{
        "type": 0,
        "value": "Credits"
      }]
    })
  }));
};
TitleSubtitleCreditsScreen.propTypes = propTypes$1;
TitleSubtitleCreditsScreen.defaultProps = defaultProps$1;

/* eslint-disable react/jsx-props-no-spreading */
var propTypes = {};
var defaultProps = {};
var TitleWithBoxScreen = function TitleWithBoxScreen(props) {
  return /*#__PURE__*/React.createElement(TitleScreen, Object.assign({}, props, {
    withSubtitle: true,
    withDescription: true,
    withBox: true
  }));
};
TitleWithBoxScreen.propTypes = propTypes;
TitleWithBoxScreen.defaultProps = defaultProps;

var definition = [{
  id: 'title',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "uKJRh2",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    order: 1
  },
  title: defineMessage({
    id: "dVcog7",
    defaultMessage: [{
      "type": 0,
      "value": "Title"
    }]
  }),
  component: TitleScreen,
  layouts: ['top', 'middle', 'bottom'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'top',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'title-subtitle',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "uKJRh2",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    order: 1
  },
  title: defineMessage({
    id: "P1dwGU",
    defaultMessage: [{
      "type": 0,
      "value": "Title with subtitle"
    }]
  }),
  component: TitleSubtitleScreen,
  layouts: ['top', 'middle', 'bottom', 'split'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'top',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'subtitle',
    type: 'heading-element',
    theme: {
      textStyle: 'heading2'
    },
    label: defineMessage({
      id: "EcZYpr",
      defaultMessage: [{
        "type": 0,
        "value": "Subtitle"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'title-subtitle-credits',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "uKJRh2",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    order: 1
  },
  title: defineMessage({
    id: "vZrFq6",
    defaultMessage: [{
      "type": 0,
      "value": "Title with subtitle and credits"
    }]
  }),
  component: TitleSubtitleCreditsScreen,
  layouts: ['top', 'middle', 'bottom', 'split-top', 'split-bottom'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'top',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'subtitle',
    type: 'heading-element',
    theme: {
      textStyle: 'heading2'
    },
    label: defineMessage({
      id: "EcZYpr",
      defaultMessage: [{
        "type": 0,
        "value": "Subtitle"
      }]
    })
  }, {
    name: 'credits',
    type: 'text-element',
    theme: {
      textStyle: 'text'
    },
    label: defineMessage({
      id: "upzOTm",
      defaultMessage: [{
        "type": 0,
        "value": "Credits"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'title-with-box',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "uKJRh2",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    order: 1
  },
  title: defineMessage({
    id: "9a5s+m",
    defaultMessage: [{
      "type": 0,
      "value": "Title with box"
    }]
  }),
  component: TitleWithBoxScreen,
  layouts: ['top', 'middle', 'bottom', 'split-top', 'split-bottom'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'top',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'subtitle',
    type: 'heading-element',
    theme: {
      textStyle: 'heading2'
    },
    label: defineMessage({
      id: "EcZYpr",
      defaultMessage: [{
        "type": 0,
        "value": "Subtitle"
      }]
    })
  }, {
    name: 'description',
    type: 'text-element',
    theme: {
      textStyle: 'text'
    },
    label: defineMessage({
      id: "ZCe0r4",
      defaultMessage: [{
        "type": 0,
        "value": "Description"
      }]
    })
  }, {
    type: 'fields',
    isList: true,
    label: defineMessage({
      id: "LOknw0",
      defaultMessage: [{
        "type": 0,
        "value": "Styles"
      }]
    }),
    fields: [{
      name: 'boxStyle',
      type: 'box-style-form',
      label: defineMessage({
        id: "f5Yipd",
        defaultMessage: [{
          "type": 0,
          "value": "Box"
        }]
      })
    }]
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "cDj1mZ",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}];

export { TitleScreen, TitleSubtitleCreditsScreen, TitleSubtitleScreen, definition as default };
